import { TEMA_PADRAO, temaAplicado, type Tema, type TemaAplicado } from '@aponte/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '../api/cliente.js';
import { useSessao } from '../autenticacao/ContextoDeSessao.js';
import {
  aplicarTema,
  lerTemaSalvo,
  observarTemaDoSistema,
  salvarTema,
  temaDoSistema,
} from './tema.js';

interface EstadoDoTema {
  /** O que a pessoa escolheu — pode ser "sistema". */
  tema: Tema;
  /** O que está pintado agora. É por ele que a tela decide qual botão está aceso. */
  aplicado: TemaAplicado;
  definirTema: (escolha: Tema) => void;
}

const Contexto = createContext<EstadoDoTema | null>(null);

/**
 * Guarda a escolha de tema e a mantém em dia nos dois lugares onde ela vale.
 *
 * Fica **dentro** do provedor de sessão e **fora** das rotas de propósito: a tela de login
 * também troca o tema, e ali ainda não há operador a quem vincular a preferência.
 */
export function ProvedorDeTema({ children }: { children: ReactNode }) {
  const { operador } = useSessao();
  const [tema, setTema] = useState<Tema>(() => lerTemaSalvo() ?? TEMA_PADRAO);
  const [aplicado, setAplicado] = useState<TemaAplicado>(() =>
    temaAplicado(lerTemaSalvo() ?? TEMA_PADRAO, temaDoSistema()),
  );

  useEffect(() => {
    setAplicado(aplicarTema(tema));
  }, [tema]);

  // Quem escolheu "sistema" acompanha o anoitecer do sistema operacional com a tela aberta.
  useEffect(() => {
    if (tema !== 'sistema') return;
    return observarTemaDoSistema(() => setAplicado(aplicarTema('sistema')));
  }, [tema]);

  /**
   * Ao entrar, a preferência da conta assume — é ela que segue a pessoa para outro
   * computador. Quando a conta ainda não tem nenhuma, quem sobe é a escolha feita neste
   * navegador: passar por cima dela com o padrão desfaria, no instante seguinte ao login,
   * o clique que a pessoa acabou de dar na tela de entrada.
   */
  const operadorId = operador?.id;
  useEffect(() => {
    if (!operadorId) return;
    let ativo = true;

    void (async () => {
      try {
        const { tema: daConta, definido } = await api.obterPreferencias();
        if (!ativo) return;

        if (definido) {
          setTema(daConta);
          salvarTema(daConta);
          return;
        }

        const escolhaLocal = lerTemaSalvo();
        if (escolhaLocal) await api.salvarPreferencias({ tema: escolhaLocal });
      } catch {
        // Preferência é conforto: falha aqui deixa valendo o tema do navegador, calado.
      }
    })();

    return () => {
      ativo = false;
    };
  }, [operadorId]);

  const definirTema = useCallback(
    (escolha: Tema) => {
      setTema(escolha);
      salvarTema(escolha);
      // Só depois de logado há a quem vincular; deslogado, a escolha fica só no navegador.
      if (operadorId) void api.salvarPreferencias({ tema: escolha }).catch(() => {});
    },
    [operadorId],
  );

  const valor = useMemo<EstadoDoTema>(
    () => ({ tema, aplicado, definirTema }),
    [tema, aplicado, definirTema],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useTema(): EstadoDoTema {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error('useTema precisa estar dentro de <ProvedorDeTema>.');
  return contexto;
}
